import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight, Truck, Store, ShoppingBag } from "lucide-react";
import { formatOrderStatus } from "@/lib/orderStatus";

type Role = "buyer" | "seller" | "driver";

interface ActiveOrder {
  orderId: string;
  deliveryId?: string;
  role: Role;
  status: string;
  counterpartyName: string;
  currentStepLabel?: string;
  tapTarget?: string;
}

interface Resp {
  activeOrders: ActiveOrder[];
}

function Group({
  title,
  icon: Icon,
  items,
  emptyHint,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: ActiveOrder[];
  emptyHint: string;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-display text-base font-bold text-foreground">{title}</h2>
        <span className="text-sm text-muted-foreground">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyHint}</p>
      ) : (
        <div className="space-y-2">
          {items.map((o, i) => (
            <Link key={`${o.orderId}-${i}`} href={o.tapTarget ?? `/orders/${o.orderId}`}>
              <Card className="transition-colors hover:bg-accent/40">
                <CardContent className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {o.counterpartyName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {o.role === "driver"
                        ? o.currentStepLabel ?? "Delivery in progress"
                        : formatOrderStatus(o.status)}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ActiveOrders() {
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ["/api/my/active-orders"],
    queryFn: async () => {
      const res = await fetch("/api/my/active-orders", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load active orders");
      return res.json();
    },
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const orders = data?.activeOrders ?? [];
  const driver = orders.filter((o) => o.role === "driver");
  const seller = orders.filter((o) => o.role === "seller");
  const buyer = orders.filter((o) => o.role === "buyer");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto w-full max-w-2xl flex-1 px-4 pb-12 pt-4">
        <h1 className="mb-1 font-display text-2xl font-bold text-foreground">Active now</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          Everything that needs your attention right now.
        </p>

        {isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              Nothing active right now. Deliveries you accept, sales to fulfill, and
              pickups due today will show up here.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {driver.length > 0 && (
              <Group title="Delivering" icon={Truck} items={driver} emptyHint="" />
            )}
            {seller.length > 0 && (
              <Group title="Selling" icon={Store} items={seller} emptyHint="" />
            )}
            {buyer.length > 0 && (
              <Group
                title="Picking up today"
                icon={ShoppingBag}
                items={buyer}
                emptyHint=""
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
